import { useNavigate, useParams } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useTypedSelector";
import { useEffect, useMemo, useState } from "react";
import {
  deleteRoute,
  generateTruckLayout,
  setRouteCustom,
  updateDriverForRoute,
} from "../redux/PlanningThunks";
import useCrudFilters from "../../../../../hooks/useCrudFilters";
import { RouteModel } from "../../domain/models/routeModel";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { setSelectedRoute } from "../../infrastructure/redux/PlanningSlice";
import { TruckModel } from "../../domain/models/truckModel";
import { getPlanningRoutes } from "../redux/thunks/route/getPlanningRoutesThunk";
import { updatePlanningRouteTruck } from "../redux/thunks/route/updatePlanningRouteTruckThunk";

export const useRouteSettingsHook = () => {
  const dispatch = useAppDispatch();
  const [truck, setTruck] = useState<TruckModel | undefined>(undefined);
  const [route, setRoute] = useState<RouteModel | undefined>(undefined);

  const navigate = useNavigate();

  const { t } = useTranslation("common");

  const { truckId, routeId } = useParams();

  const { routes } = useAppSelector((state) => state.planning);

  useEffect(() => {
    if (routes.length === 0) {
      void dispatch(getPlanningRoutes());
    }
  }, []);

  useEffect(() => {
    if (routes.length > 0 && truck == undefined && route == undefined) {
      const route = routes.find((route) => route.id === routeId);

      if (!route) {
        navigate(-1);
      }

      // when routeId is not a number, it means the route is not created yet
      if (isNaN(Number(routeId)) || route?.truck == null) {
        setRoute(route);
        return;
      } else {
        setTruck(route.truck);

        void dispatch(generateTruckLayout(route?.id ?? ""))
          .then((res) => {
            const updatedRoute = res.payload as RouteModel;
            setRoute({
              ...route,
              layout: updatedRoute.layout,
              custom: updatedRoute.custom,
            } as RouteModel);
          })
          .catch(() => {
            setRoute(route);
          });
      }
    }
  }, [routes, truckId, routeId, truck, route]);

  const userFilters = useCrudFilters({
    searchBy: "SearchByRootUser",
    fields: {
      roles: {
        fields: {
          name: {
            in: ["dbh-employee", "dbh-manager", "dbh-driver"],
          },
        },
      },
    },
  });

  const removeRoute = (route: RouteModel | undefined) => {
    void swal({
      title: t("crud.routes.delete.confirm"),
      buttons: {
        cancel: {
          visible: true,
          className: "btn btn-light btn-sm",
        },
        confirm: {
          visible: true,
          className: "btn btn-primary btn-sm",
        },
      },
    }).then((willDelete) => {
      if (willDelete) {
        if (route) {
          void dispatch(deleteRoute(route));
        }
        void dispatch(setSelectedRoute(undefined));

        navigate(-1);
      }
    });
  };

  const setDriverId = ({ value, label }: { value: string; label: string }) => {
    if (route) {
      void dispatch(
        updateDriverForRoute({
          route: route,
          id: value,
          label: label,
        }),
      );
    }
  };

  const turnOnGeneration = () => {
    if (route) {
      void dispatch(setRouteCustom(route)).then(() => {
        if (truck) {
          updateRouteTruck(truck);
        }
      });
    }
  };

  const updateRouteCustom = () => {
    if (route) {
      setRoute({ ...route, custom: true });
    }
  };

  const isValidRoute = useMemo(() => {
    return !route?.id.includes("state");
  }, [route]);

  const isValidRouteForLayout = useMemo(() => {
    return (
      isValidRoute &&
      route?.truck?.width &&
      route?.truck?.length &&
      route?.packingSlips?.length > 0
    );
  }, [isValidRoute, route]);

  const updateRouteTruck = (truck: TruckModel) => {
    if (route) {
      void dispatch(updatePlanningRouteTruck({ route, truck })).finally(() => {
        setRoute(undefined);
        setTruck(undefined);
      });
    }
  };

  return {
    truck,
    route,
    userFilters,
    removeRoute,
    setDriverId,
    isValidRoute,
    isValidRouteForLayout,
    updateRouteTruck,
    turnOnGeneration,
    updateRouteCustom,
  };
};
